import { Alert, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Controller } from 'react-hook-form';
import { customMutator } from '../../../api/mutator.js';
import { resolveTKey } from '../../../i18n/resolve-key.js';
import type { FieldAdapterProps } from '../../../runtime/forms/field-adapter-registry';

interface SourceLot {
  id: string;
  availableQuantity: string;
  acquisitionDate: string;
}
interface Destination {
  id: string;
  accountNumber: string | null;
  accountType: string;
  baseCurrency: string;
}
interface Eligibility {
  destinationPortfolioAccounts: Destination[];
  sourceLots: SourceLot[];
}
interface Plan {
  destinationPortfolioAccountId: string;
  allocations: Array<{ sourceLotId: string; quantity: string }>;
}

const EMPTY_ALLOCATION = { sourceLotId: '', quantity: '' } as const;

function normalizePlan(value: unknown): Plan {
  const candidate = value && typeof value === 'object' ? (value as Partial<Plan>) : {};
  return {
    destinationPortfolioAccountId:
      typeof candidate.destinationPortfolioAccountId === 'string'
        ? candidate.destinationPortfolioAccountId
        : '',
    allocations:
      Array.isArray(candidate.allocations) && candidate.allocations.length > 0
        ? candidate.allocations.map(allocation => ({
            sourceLotId:
              allocation && typeof allocation.sourceLotId === 'string'
                ? allocation.sourceLotId
                : '',
            quantity:
              allocation && typeof allocation.quantity === 'string' ? allocation.quantity : '',
          }))
        : [{ ...EMPTY_ALLOCATION }],
  };
}

const DECIMAL = /^(?:\d+(?:\.\d{1,4})?|\.\d{1,4})$/;
const positive = (value: string) =>
  DECIMAL.test(value.trim()) && value.replace(/[.0]/g, '').length > 0;
const fixed = (value: string) => {
  const [whole = '0', fraction = ''] = value.trim().replace(/^\./, '0.').split('.');
  return BigInt(`${whole}${fraction.padEnd(4, '0')}`);
};
function validAllocation(row: Plan['allocations'][number], lots: SourceLot[]) {
  const lot = lots.find(candidate => candidate.id === row.sourceLotId);
  return Boolean(
    lot && positive(row.quantity) && fixed(row.quantity) <= fixed(lot.availableQuantity)
  );
}

/** Specialist financial adapter. It owns only eligibility-bound lot editing; form/action identity stays resolved. */
export function PositionTransferLotsAdapter(props: FieldAdapterProps) {
  const { field, control, formValues, pending, state, t, formState } = props;
  const source = String(formValues?.sourcePortfolioAccountId ?? '').trim();
  const instrument = String(formValues?.instrumentSymbol ?? '').trim();
  const query = useQuery({
    queryKey: ['position-transfer-request-eligibility', source, instrument],
    enabled: Boolean(source && instrument),
    queryFn: async () =>
      (
        await customMutator<{ data: Eligibility }>({
          url: `/api/position-transfers/request-eligibility?sourcePortfolioAccountId=${encodeURIComponent(source)}&instrumentSymbol=${encodeURIComponent(instrument)}`,
          method: 'GET',
        })
      ).data,
    retry: false,
  });
  if (!control) return null;
  return (
    <Controller
      control={control}
      name={field.name}
      defaultValue={{
        destinationPortfolioAccountId: '',
        allocations: [{ sourceLotId: '', quantity: '' }],
      }}
      rules={{
        validate: (value: Plan) => {
          if (!value?.destinationPortfolioAccountId || !value.allocations?.length)
            return t('validation.required');
          if (
            new Set(value.allocations.map(row => row.sourceLotId)).size !== value.allocations.length
          )
            return t(resolveTKey('validation.pattern'));
          return (
            value.allocations.every(row => validAllocation(row, query.data?.sourceLots ?? [])) ||
            t(resolveTKey('validation.pattern'))
          );
        },
      }}
      render={({ field: controller }) => {
        const plan = normalizePlan(controller.value);
        const disabled = pending || state.disabled || query.isFetching;
        const update = (next: Plan) => controller.onChange(next);
        return (
          <Stack spacing={1.5} data-testid="position-transfer-lot-allocation">
            {query.isError && (
              <Alert severity="error">{t('card:positionTransfers.requestEligibilityFailed')}</Alert>
            )}
            <TextField
              select
              fullWidth
              required
              disabled={disabled}
              label={t('card:positionTransfers.fields.destinationPortfolioAccountId')}
              value={plan.destinationPortfolioAccountId}
              error={Boolean(formState.errors[field.name])}
              onChange={event =>
                update({ ...plan, destinationPortfolioAccountId: event.target.value })
              }
            >
              {(query.data?.destinationPortfolioAccounts ?? []).map(account => (
                <MenuItem key={account.id} value={account.id}>
                  {[account.accountNumber, account.accountType, account.baseCurrency]
                    .filter(Boolean)
                    .join(' — ')}
                </MenuItem>
              ))}
            </TextField>
            <Typography component="h3" variant="subtitle2">
              {t('card:positionTransfers.allocations')}
            </Typography>
            {plan.allocations.map((row, index) => (
              <Stack
                key={`${row.sourceLotId}-${index}`}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
              >
                <TextField
                  select
                  fullWidth
                  required
                  disabled={disabled}
                  label={t('card:positionTransfers.fields.sourceLotId')}
                  value={row.sourceLotId}
                  onChange={event =>
                    update({
                      ...plan,
                      allocations: plan.allocations.map((candidate, i) =>
                        i === index ? { ...candidate, sourceLotId: event.target.value } : candidate
                      ),
                    })
                  }
                >
                  {(query.data?.sourceLots ?? []).map(lot => (
                    <MenuItem
                      key={lot.id}
                      value={lot.id}
                      disabled={plan.allocations.some(
                        (candidate, i) => i !== index && candidate.sourceLotId === lot.id
                      )}
                    >
                      {lot.acquisitionDate} — {lot.availableQuantity}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth
                  required
                  label={t('card:positionTransfers.fields.quantity')}
                  value={row.quantity}
                  error={
                    Boolean(row.quantity) && !validAllocation(row, query.data?.sourceLots ?? [])
                  }
                  onChange={event =>
                    update({
                      ...plan,
                      allocations: plan.allocations.map((candidate, i) =>
                        i === index ? { ...candidate, quantity: event.target.value } : candidate
                      ),
                    })
                  }
                  slotProps={{
                    htmlInput: {
                      inputMode: 'decimal',
                      pattern: '(?:[0-9]+(?:[.][0-9]{1,4})?|[.][0-9]{1,4})',
                      autoComplete: 'off',
                    },
                  }}
                />
                <Button
                  disabled={disabled || plan.allocations.length === 1}
                  onClick={() =>
                    update({ ...plan, allocations: plan.allocations.filter((_, i) => i !== index) })
                  }
                >
                  {t('card:positionTransfers.actions.removeAllocation')}
                </Button>
              </Stack>
            ))}
            <Button
              variant="outlined"
              disabled={disabled}
              onClick={() =>
                update({
                  ...plan,
                  allocations: [...plan.allocations, { sourceLotId: '', quantity: '' }],
                })
              }
            >
              {t('card:positionTransfers.actions.addAllocation')}
            </Button>
          </Stack>
        );
      }}
    />
  );
}
